import React, { useState } from "react";
import {
  KNOWLEDGE_FILE_ACCEPT,
  KNOWLEDGE_FILE_LABEL,
  knowledgeFileDuplicateMessage,
  knowledgeFileRejectionMessage,
  partitionDuplicateKnowledgeFiles,
  partitionKnowledgeFiles,
} from "../utils/knowledgeFileTypes";
import { useToast } from "../../../context/ToastContext";

interface DragAndDropUploaderProps {
  onFilesAdded: (files: File[]) => void;
}

const DragAndDropUploader: React.FC<DragAndDropUploaderProps> = ({
  onFilesAdded,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);
  const { showToast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { accepted, rejectedNames } = partitionKnowledgeFiles(
      Array.from(e.target.files || [])
    );
    setRejectionMessage(knowledgeFileRejectionMessage(rejectedNames));

    // Picking the same file twice before submitting would attach two copies of it —
    // each gets a distinct timestamped name on upload, so nothing downstream notices.
    const { fresh, duplicateNames } = partitionDuplicateKnowledgeFiles(
      accepted,
      files.map((f) => f.name)
    );
    const duplicateMessage = knowledgeFileDuplicateMessage(duplicateNames);
    if (duplicateMessage) showToast(duplicateMessage, "warning");

    const newFiles = [...files, ...fresh];
    setFiles(newFiles);
    onFilesAdded(newFiles);
    // Let the same file be re-picked after a removal.
    e.target.value = "";
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesAdded(updatedFiles);
  };

  return (
    <div className="w-full">
      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-700 mb-4">
        Knowledge Base <span className="font-normal text-gray-500">(Optional)</span>
      </h3>

      {/* Drag and Drop Area */}
      <div className="flex items-center justify-center w-full">
        <label
          htmlFor="dropzone-file"
          className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-surface-ui-hover hover:border-brand-primary transition-colors"
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <svg
              className="w-8 h-8 mb-4 text-gray-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 16"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
              />
            </svg>
            <p className="mb-2 text-sm text-gray-700">
              <span className="font-semibold">Click to upload</span> or drag and
              drop
            </p>
            <p className="text-xs text-gray-500">
              {KNOWLEDGE_FILE_LABEL} (Max. 10MB)
            </p>
            <p className="mt-1 text-[11px] text-gray-400">
              Formats we can read text from and index for long-term recall
            </p>
          </div>
          <input
            id="dropzone-file"
            type="file"
            className="hidden"
            accept={KNOWLEDGE_FILE_ACCEPT}
            multiple
            onChange={handleFileChange}
          />
        </label>
      </div>

      {rejectionMessage && (
        <p className="mt-2 text-xs text-red-600">{rejectionMessage}</p>
      )}

      {/* File List with Remove Option */}
      <div className="mt-4">
        {files.length > 0 && (
          <ul className="text-sm text-gray-700 space-y-2">
            {files.map((file, index) => (
              <li
                key={index}
                className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md p-2"
              >
                <span className="truncate">{file.name}</span>
                <button
                  onClick={() => handleRemoveFile(index)}
                  className="text-red-600 hover:text-red-700 text-xs transition-colors"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default DragAndDropUploader;
